import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { GraphqlResponse } from '@interfaces/graphql-response';
import { ErrorHandlerService } from '@services/error-handler.service';
import { DocumentNode, print } from 'graphql/language';
import { mergeMap, Observable, of, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GraphqlService {
  private http = inject(HttpClient);
  private errorHandler = inject(ErrorHandlerService);

  /**
   * Executes a GraphQL query or mutation using a DocumentNode.
   *
   * @param document The GraphQL query or mutation as a DocumentNode.
   * @param variables An optional object containing the variables for the GraphQL operation.
   * @param httpOptions Optional HTTP options to customize the request (e.g., headers).
   */
  public execute<TData, TVars>(document: DocumentNode, variables?: TVars, httpOptions: object = {}): Observable<TData> {
    const query = print(document);
    const operationName = this.extractOperationName(document);

    return this.http
      .post<
        GraphqlResponse<TData>
      >(environment.analyteCareApi + '/graphql', JSON.stringify({ query, operationName, variables }), this.withJsonContentTypeHeader(httpOptions))
      .pipe(
        mergeMap((response) => {
          if (response.errors?.length) {
            const errors = this.errorHandler.parseGraphQLResponseErrors(response.errors);
            return throwError(() => new Error(`GraphQL Errors: ${errors}. Query: ${query}`));
          }

          if (response.data == null) {
            return throwError(() => new Error(`No data received from GraphQL endpoint. Query: ${query}`));
          }

          return of(response.data);
        }),
      );
  }

  /**
   * Utility method to add the 'Content-Type: application/json' header to the given httpOptions object.
   */
  private withJsonContentTypeHeader(
    httpOptions: { headers?: HttpHeaders | Record<string, string | string[]> } = {},
  ): object {
    const baseHeaders =
      httpOptions.headers instanceof HttpHeaders ? httpOptions.headers : new HttpHeaders(httpOptions.headers ?? {});

    return {
      ...httpOptions,
      headers: baseHeaders.set('Content-Type', 'application/json'),
    };
  }

  /**
   * Extract operation name from a DocumentNode.
   *
   * @param document The GraphQL DocumentNode
   */
  private extractOperationName(document: DocumentNode): string | null {
    const definition = document.definitions.find((def) => def.kind === 'OperationDefinition');

    if (definition && 'name' in definition && definition.name) {
      return definition.name.value;
    }

    return null;
  }
}
